import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { api, apiError, inr, logError, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Layout";
import {
  InactiveUserActions, UserDeactivateDialog, UserEditDialog,
} from "@/components/UserAdmin";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function Suppliers() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    company: "", contact_person: "", mobile: "", whatsapp: "", email: "", gstin: "",
    address: "", city: "", state: "", payment_terms: "", notes: "",
  });

  const load = useCallback(
    () =>
      api
        .get("/suppliers")
        .then(({ data }) => setItems(data))
        .catch((e) => toast.error(apiError(e))),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      if (!form.company.trim()) return toast.error("Supplier name is required");
      await api.post("/suppliers", form);
      toast.success("Supplier created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="suppliers-header"
        title="Suppliers"
        subtitle={`${items.length} suppliers`}
        actions={
          can("suppliers", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-supplier-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Supplier
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle>New supplier</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["company", "Company", "supplier-company-input"],
                    ["contact_person", "Contact person", null],
                    ["mobile", "Mobile", "supplier-mobile-input"],
                    ["whatsapp", "WhatsApp", null],
                    ["email", "Email", null],
                    ["gstin", "GSTIN", null],
                    ["city", "City", null],
                    ["state", "State", null],
                    ["payment_terms", "Payment terms", null],
                  ].map(([k, l, tid]) => (
                    <Field key={k} label={l}>
                      <Input
                        data-testid={tid || undefined}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </Field>
                  ))}
                  <Field label="Address" className="sm:col-span-2">
                    <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </Field>
                </div>
                <DialogFooter>
                  <Button data-testid="supplier-save-button" onClick={save}>
                    Save supplier
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />
      <div className="p-4 sm:p-6">
        {items.length === 0 ? (
          <Empty title="No suppliers yet" />
        ) : (
          <div className="surface overflow-x-auto" data-testid="suppliers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="hidden md:table-cell">GSTIN</TableHead>
                  <TableHead className="hidden lg:table-cell">Terms</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                    <TableCell className="text-sm font-medium">
                      {s.company}
                      <div className="text-xs text-muted-foreground">{s.code}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {s.contact_person}
                      <div className="text-xs text-muted-foreground">{s.mobile}</div>
                    </TableCell>
                    <TableCell className="hidden font-mono-num text-xs md:table-cell">{s.gstin || "-"}</TableCell>
                    <TableCell className="hidden text-xs lg:table-cell">{s.payment_terms || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

export function Projects() {
  const [items, setItems] = useState([]);
  const [expenses, setExpenses] = useState({});

  useEffect(() => {
    api.get("/projects").then(async ({ data }) => {
      setItems(data);
      const map = {};
      for (const p of data) {
        try {
          const { data: e } = await api.get(`/expenses?project_id=${p.id}`);
          map[p.id] = e.total;
        } catch {
          map[p.id] = 0;
        }
      }
      setExpenses(map);
    });
  }, []);

  return (
    <div>
      <PageHeader
        testId="projects-header"
        title="Projects"
        subtitle="Revenue, cost and milestone progress for every installation project."
      />
      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-2">
        {items.length === 0 && (
          <Empty title="No projects yet" hint="Projects are created automatically when a quotation converts to an order." />
        )}
        {items.map((p) => {
          const done = (p.milestones || []).filter((m) => m.status === "Completed").length;
          const total = (p.milestones || []).length || 1;
          const cost = expenses[p.id] || 0;
          const profit = Number(p.value || 0) - cost;
          return (
            <div key={p.id} className="surface p-5" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="overline">{p.code}</div>
                  <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                  <div className="text-xs text-muted-foreground">{p.customer_name}</div>
                </div>
                <Badge variant="outline">{p.status}</Badge>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="overline">Revenue</div>
                  <div className="font-mono-num text-sm">{inr(p.value)}</div>
                </div>
                <div>
                  <div className="overline">Cost booked</div>
                  <div className="font-mono-num text-sm">{inr(cost)}</div>
                </div>
                <div>
                  <div className="overline">Est. profit</div>
                  <div className={`font-mono-num text-sm ${profit < 0 ? "text-destructive" : "text-emerald-600"}`}>
                    {inr(profit)}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>Milestones</span>
                  <span>
                    {done}/{total}
                  </span>
                </div>
                <Progress value={(done / total) * 100} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(p.milestones || []).map((m) => (
                  <Badge
                    key={m.name}
                    variant={m.status === "Completed" ? "default" : m.status === "In Progress" ? "secondary" : "outline"}
                    className="text-[9px]"
                  >
                    {m.name}
                  </Badge>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SalesOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/sales-orders").then(({ data }) => setItems(data));
  }, []);
  return (
    <div>
      <PageHeader testId="orders-header" title="Sales Orders" subtitle={`${items.length} orders`} />
      <div className="p-4 sm:p-6">
        {items.length === 0 ? (
          <Empty title="No sales orders" hint="Convert an approved quotation to create an order." />
        ) : (
          <div className="surface overflow-x-auto" data-testid="orders-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="hidden md:table-cell">Quotation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell className="font-mono-num text-xs">
                      {o.number}
                      <div className="text-[10px] text-muted-foreground">{shortDate(o.created_at)}</div>
                    </TableCell>
                    <TableCell className="text-sm">{o.customer_name}</TableCell>
                    <TableCell className="hidden font-mono-num text-xs md:table-cell">{o.quotation_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {o.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm">{inr(o.grand_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

export function Employees() {
  const { can, user } = useAuth();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rights, setRights] = useState({});
  const [view, setView] = useState("active");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "employee", department: "", designation: "",
    mobile: "", whatsapp: "", location: "", reporting_manager: "", is_active: true,
  });

  const load = useCallback(
    () =>
      api
        .get(`/user-admin/employees?view=${view}`)
        .then(({ data }) => {
          setItems(data.employees);
          setRights(data.rights);
          setRoles((r) => (r.length ? r : data.roles));
        })
        .catch((e) => toast.error(apiError(e))),
    [view]
  );

  useEffect(() => {
    load();
    api
      .get("/auth/roles")
      .then(({ data }) => setRoles(data))
      .catch((e) => logError("role list", e));
  }, [load]);

  const save = async () => {
    try {
      await api.post("/employees", form);
      toast.success("Employee created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="employees-header"
        title="Employees & Users"
        subtitle="Roles, permissions and the WhatsApp numbers AI uses to identify employees."
        actions={
          rights.can_manage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-employee-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle>New employee</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["name", "Name", "employee-name-input"],
                    ["email", "Email", "employee-email-input"],
                    ["mobile", "Mobile", null],
                    ["whatsapp", "WhatsApp number", "employee-whatsapp-input"],
                    ["department", "Department", null],
                    ["designation", "Designation", null],
                    ["location", "Factory / site", null],
                  ].map(([k, l, tid]) => (
                    <Field key={k} label={l}>
                      <Input
                        data-testid={tid || undefined}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </Field>
                  ))}
                  <Field label="Role" className="sm:col-span-2">
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="employee-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="employee-password-note">
                  No password is set here. The employee signs in with this email and creates their
                  own password the first time.
                </p>
                <DialogFooter>
                  <Button data-testid="employee-save-button" onClick={save}>
                    Create employee
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 sm:px-6">
        <div className="flex rounded-md border border-border p-0.5" data-testid="employees-view-toggle">
          <button type="button" data-testid="view-active-employees"
            className={`rounded px-3 py-1 text-xs ${view === "active" ? "bg-primary text-primary-foreground" : ""}`}
            onClick={() => setView("active")}>Active employees</button>
          <button type="button" data-testid="view-inactive-employees"
            className={`rounded px-3 py-1 text-xs ${view === "inactive" ? "bg-primary text-primary-foreground" : ""}`}
            onClick={() => setView("inactive")}>Inactive / archived</button>
        </div>
        {!rights.can_manage && (
          <Badge variant="outline" className="text-[10px]" data-testid="employees-readonly-badge">
            View only — the Owner has not granted you user-management rights
          </Badge>
        )}
      </div>
      <div className="p-4 sm:p-6">
        <div className="surface overflow-x-auto" data-testid="employees-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Department</TableHead>
                <TableHead className="hidden lg:table-cell">WhatsApp</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                  <TableCell className="text-sm font-medium">
                    {e.name}
                    {e.is_owner && (
                      <Badge className="ml-1 text-[9px]" data-testid={`owner-badge-${e.id}`}>
                        OWNER
                      </Badge>
                    )}
                    <div className="text-xs text-muted-foreground">{e.email}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {e.role_label}
                    {(e.can_manage_users || e.can_delete_users || e.can_purge_users) && !e.is_owner && (
                      <div className="text-[10px] text-muted-foreground">
                        {[e.can_manage_users && "manage", e.can_delete_users && "deactivate",
                          e.can_purge_users && "purge"].filter(Boolean).join(" • ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-xs md:table-cell">{e.department || "—"}</TableCell>
                  <TableCell className="hidden font-mono-num text-xs lg:table-cell">
                    {e.whatsapp || "—"}
                    {e.whatsapp_bot_revoked && (
                      <Badge variant="destructive" className="ml-1 text-[9px]"
                        data-testid={`whatsapp-revoked-${e.id}`}>
                        bot off
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={e.is_active && !e.is_deleted ? "default" : "destructive"}
                      className="text-[10px]">
                      {e.is_deleted ? "Deactivated" : e.is_active ? "Active" : "Inactive"}
                    </Badge>
                    {e.must_change_password && (
                      <Badge variant="outline" className="ml-1 text-[9px]">must change</Badge>
                    )}
                    {!e.password_set && (
                      <Badge variant="outline" className="ml-1 text-[9px]" data-testid={`no-password-badge-${e.id}`}>
                        password not set
                      </Badge>
                    )}
                    {e.delete_reason && (
                      <div className="text-[10px] text-muted-foreground">{e.delete_reason}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {view === "inactive" ? (
                      <InactiveUserActions employee={e} canDelete={rights.can_delete}
                        canPurge={rights.can_purge} onChanged={load} />
                    ) : (
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {rights.can_manage && (
                          <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                            data-testid={`edit-user-${e.id}`} onClick={() => setEditing(e)}>
                            <Pencil className="h-3 w-3" /> Edit
                          </Button>
                        )}
                        {rights.can_delete && !e.is_owner && e.id !== user?.id && (
                          <Button size="sm" variant="ghost"
                            className="h-7 gap-1 px-2 text-[11px] text-destructive"
                            data-testid={`deactivate-user-${e.id}`}
                            onClick={() => setDeactivating(e)}>
                            <Trash2 className="h-3 w-3" /> Deactivate
                          </Button>
                        )}
                        {can("settings", "edit") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px]"
                            data-testid={`reset-password-button-${e.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Clear the password for ${e.email}? They will create a new one at next sign-in.`)) return;
                              try {
                                const { data } = await api.post(`/auth/admin/reset-password/${e.id}`);
                                setTempPassword(data);
                                load();
                              } catch (err) {
                                toast.error(apiError(err));
                              }
                            }}
                          >
                            Reset password
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {editing && (
        <UserEditDialog employee={editing} roles={roles} isOwner={!!rights.is_owner}
          onClose={() => setEditing(null)} onSaved={load} />
      )}
      {deactivating && (
        <UserDeactivateDialog employee={deactivating} onClose={() => setDeactivating(null)}
          onDone={load} />
      )}

      <Dialog open={!!tempPassword} onOpenChange={(o) => !o && setTempPassword(null)}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Password cleared</DialogTitle>
          </DialogHeader>
          <p className="text-sm" data-testid="password-cleared-message">
            <strong>{tempPassword?.email}</strong> can now sign in with just their email and create
            their own new password. No password is stored or shared by anyone else.
          </p>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const REPORTS = [
  ["sales", "Sales orders"],
  ["quotations", "Quotations"],
  ["inventory", "Inventory valuation"],
  ["purchase", "Purchases"],
  ["expenses", "Expenses"],
  ["stock-movement", "Stock movement"],
];

export function Reports() {
  const [kind, setKind] = useState("quotations");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/reports/${k}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(kind);
  }, [kind]);

  const exportCsv = () => {
    const rows = data?.rows || [];
    if (!rows.length) return toast.error("Nothing to export");
    const keys = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object");
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `pramsia-${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rows = data?.rows || [];
  const keys = rows.length ? Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object").slice(0, 8) : [];

  return (
    <div>
      <PageHeader
        testId="reports-header"
        title="Reports"
        subtitle="Financial-year data with CSV export."
        actions={
          <>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="report-kind-select" className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORTS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="report-export-button" onClick={exportCsv}>
              Export CSV
            </Button>
          </>
        }
      />
      <div className="p-4 sm:p-6">
        <div className="mb-3 text-sm text-muted-foreground">
          {rows.length} rows • total{" "}
          <span className="font-mono-num text-foreground">
            {kind === "stock-movement" ? data?.total : inr(data?.total)}
          </span>
        </div>
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <Empty title="No data for this report" />
        ) : (
          <div className="surface overflow-x-auto" data-testid="report-table">
            <Table>
              <TableHeader>
                <TableRow>
                  {keys.map((k) => (
                    <TableHead key={k}>{k.replace(/_/g, " ")}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.slice(0, 200).map((r, i) => (
                  <TableRow key={r.id || i}>
                    {keys.map((k) => (
                      <TableCell key={k} className="text-xs">
                        {String(r[k] ?? "—").slice(0, 40)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
